import { FC, useState } from 'react';
import Joyride, {
  ACTIONS, CallBackProps, EVENTS, STATUS, Step,
} from 'react-joyride';
import { useTranslation } from 'react-i18next';
import { FONT_PRIMARY } from '../data';

interface ITourStep extends Step {
  skipStep?: boolean
}

type TTourGuideProps = {
  steps: ITourStep[];
  run: boolean;
  onFinish: () => void;
  actions?: Map<number, () => void>;
};

const TourGuide: FC<TTourGuideProps> = ({
  steps, run, onFinish, actions = new Map(),
}) => {
  const { t } = useTranslation(['common', 'tour']);
  const [stepIndex, setStepIndex] = useState(0);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const {
      action, index, status, type,
    } = data;

    if (type === EVENTS.STEP_AFTER) {
      // Check and execute the action for the current step before showing the tooltip
      if (actions.has(index)) {
        actions.get(index)?.(); // Execute action for this specific step
      }

      // Skip to the next step if `skipStep` is true for this step
      if (steps[index].skipStep) {
        setStepIndex(index + 1); // Move to the next step
        return;
      }
    }

    if (([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as string[]).includes(type)) {
      // Update the step index after performing the action
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    } else if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
      onFinish();
      setStepIndex(0);
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      scrollToFirstStep
      stepIndex={stepIndex}
      disableScrolling
      showProgress
      showSkipButton={false}
      locale={{
        back: t('back'),
        next: t('next'),
        last: t('finish', { ns: 'tour' }),
      }}
      styles={{
        options: {
          primaryColor: '#A855F7',
          zIndex: 99999,
        },
        tooltipContent: {
          fontFamily: FONT_PRIMARY,
          maxWidth: 800,
        },
        tooltip: {
          width: 'auto',
        },
      }}
      callback={handleJoyrideCallback}
    />
  );
};

export { TourGuide };
